import { FC, PropsWithChildren as PWC } from 'react'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'

import IconButton from '~/src/components/generic/IconButton'
import { Icon } from '~/src/components/generic/PhosphorIcons'
import Button from '~/src/components/generic/Button'

import { tw } from '~/lib/utils'
import colors from '~/config/colorsScheme'

type Props = {
  isOpen: boolean
  title: string
  primaryButtonText: string
  secondaryButtonText?: string
  isPrimaryButtonDisabled?: boolean
  loading: boolean
  className?: string
  preventOutSideClose?: boolean
  contentPadding?: 'default' | 'none'
  onClose: () => void
  onConfirm?: () => void
}
const BaseDialog: FC<PWC<Props>> = ({
  children,
  contentPadding = 'default',
  isOpen,
  title,
  primaryButtonText,
  secondaryButtonText = 'Cancel',
  loading,
  className,
  preventOutSideClose = false,
  onClose,
  onConfirm,
  isPrimaryButtonDisabled = false,
}) => {
  const handleClose = () => {
    if (preventOutSideClose) {
      return
    }

    onClose()
  }

  return (
    <Dialog
      className="relative z-50"
      data-testid="base-dialog"
      open={isOpen}
      onClose={handleClose}
    >
      <DialogBackdrop
        className="fixed inset-0 bg-black/25 duration-300 ease-out data-[closed]:opacity-0"
        transition
      />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="insert-0 flex min-h-full items-center justify-center p-4 text-center">
          <DialogPanel
            className={tw(
              'rounded bg-white text-left align-middle shadow-xl duration-300 ease-out data-[closed]:scale-95 data-[closed]:opacity-0',
              className
            )}
            transition
          >
            <div className="flex items-center border-b border-divider px-5 py-3">
              <DialogTitle className="flex-1 text-center text-heading-s text-default">
                {title}
              </DialogTitle>
              <IconButton aria-label="Close Dialog" variant="ghost" onClick={onClose}>
                <Icon color={colors.gray[700]} name="XIcon" size="20" />
              </IconButton>
            </div>
            <div className={tw('p-6', contentPadding === 'none' && 'p-0')}>
              {children}
            </div>

            {onConfirm && (
              <div className="mb-4 flex justify-end space-x-4 px-8 py-1">
                <Button size="md" variant="secondary" onClick={onClose}>
                  {secondaryButtonText}
                </Button>
                <Button
                  disabled={isPrimaryButtonDisabled}
                  loading={loading}
                  size="md"
                  variant="primary"
                  onClick={onConfirm}
                >
                  {primaryButtonText}
                </Button>
              </div>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}

export default BaseDialog
